import { Link } from "react-router-dom";
import Avatar from "./Avatar";
import FallbackImage from "./FallbackImage";

const toneClasses = {
  genre: "bg-blue-600/90 text-white",
  mood: "bg-fuchsia-600/90 text-white",
  city: "bg-slate-700 text-slate-200",
  visibility: "bg-slate-700 text-slate-200",
  draft: "bg-amber-500/90 text-white",
  ai: "bg-violet-600/90 text-white",
};

// Shared dense card used by Events, News and Reviews.
function ContentCard({
  imageUrl,
  fallbackImageUrl,
  badges = [],
  title,
  subtitle,
  excerpt,
  authorName,
  authorId,
  authorPhotoUrl,
  authorAvatarConfig,
  postedLabel,
  noImageLabel = "No image",
  authorPrefix = "By",
  actions,
  ticketUrl,
  ticketLabel = "Buy tickets",
  onCardClick,
}) {
  const clickable = typeof onCardClick === "function";

  return (
    <article
      onClick={onCardClick}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onCardClick(e);
              }
            }
          : undefined
      }
      role={clickable ? "button" : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`glass-card relative flex h-full min-h-[300px] flex-col rounded-3xl border border-slate-500/40 bg-slate-800/60 p-4 text-sm shadow-md shadow-black/20 transition-all duration-200 hover:border-sky-400/40 hover:shadow-xl hover:shadow-black/30 md:hover:-translate-y-0.5 ${
        clickable ? "cursor-pointer" : ""
      }`}
    >
      {postedLabel && (
        <div className="absolute right-4 top-4 text-[10px] uppercase tracking-[0.2em] text-slate-400">
          {postedLabel}
        </div>
      )}
      <div className="mb-3 flex flex-wrap gap-1.5 pr-20">
        {badges.map((badge, index) => (
          <span
            key={`${badge.label}-${index}`}
            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest ${
              toneClasses[badge.tone] || toneClasses.city
            }`}
          >
            {badge.label}
          </span>
        ))}
      </div>
      <FallbackImage
        src={imageUrl}
        fallbackSrc={fallbackImageUrl}
        alt=""
        className="mb-3 h-32 w-full rounded-2xl border border-slate-700/60 object-cover bg-slate-800"
        placeholderClassName="mb-3 flex h-32 w-full items-center justify-center rounded-2xl border border-slate-700/60 bg-slate-800 text-[11px] text-slate-500"
        placeholderLabel={noImageLabel}
      />
      <h3 className="mb-1 line-clamp-2 text-lg font-semibold text-white">{title}</h3>
      {subtitle && <p className="mb-2 text-xs text-slate-300">{subtitle}</p>}
      {excerpt ? (
        <p className="line-clamp-3 flex-grow text-xs leading-5 text-slate-300">{excerpt}</p>
      ) : (
        <div className="flex-grow" />
      )}
      {authorName && (
        <p
          className="mt-3 flex items-center gap-2 text-xs text-slate-400"
          onClick={(e) => e.stopPropagation()}
        >
          <Avatar src={authorPhotoUrl} avatarConfig={authorAvatarConfig} size={20} alt={authorName} />
          {`${authorPrefix} `}
          <Link
            to={`/profile?uid=${authorId}`}
            className="text-sky-300 transition hover:text-sky-200 hover:underline"
          >
            {authorName}
          </Link>
        </p>
      )}
      {ticketUrl && (
        <a
          href={ticketUrl}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="mt-3 inline-flex items-center justify-center rounded-xl bg-gradient-to-r from-emerald-600 to-green-600 px-4 py-2 text-xs font-semibold text-white shadow transition hover:from-emerald-500 hover:to-green-500"
        >
          🎟️ {ticketLabel}
        </a>
      )}
      {actions && (
        <div className="mt-3 flex justify-end gap-3" onClick={(e) => e.stopPropagation()}>
          {actions}
        </div>
      )}
    </article>
  );
}

export default ContentCard;
